import React, { useState, useEffect } from 'react';
import http from '../components/http/http';
import ProfileLayout from './ProfileLayout';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

const Title = styled.h1`
  font-size: 38px !important;
  font-style: normal;
  font-weight: 700;
  line-height: 52px;
  letter-spacing: 0.8px;
`;
const Field = styled.div`
  margin-bottom: 24px;
`;
const Label = styled.label`
  display: block;
  font-size: 14px;
  color: #888;
`;
const Input = styled.input<{disabled?: boolean}>`
  width: 100%;
  padding: 12px 16px;
  font-size: 14px;
  border: 1px solid #ededed;
  border-radius: 6px;
  background: ${({disabled}) => disabled ? '#f7f7f7' : '#fff'};
  color: #222;
  outline: none;
  transition: border .2s;
  &:focus {
    border-color: #3baab0;
  }
`;
const Button = styled.button`
  padding: 12px 32px;
  background: #3baab0;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  margin-right: 16px;
  transition: background .2s;
  &:hover {
    background: #218b8f;
  }
`;

export default function Profile() {
  const { t } = useTranslation();
  const [edit, setEdit] = useState(false);
  const [fields, setFields] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '********',
    phone: '',
  });
  const [originalFields, setOriginalFields] = useState(fields);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [userId, setUserId] = useState<number | null>(null);
  const [userMeta, setUserMeta] = useState<any>({});

  useEffect(() => {
    http.get('/api/users/auth/me').then((user: any) => {
      console.log('Profile /me user:', user);
      if (user && user.email) {
        const data = {
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          email: user.email || '',
          password: '********',
          phone: user.phoneNumber || '',
        };
        setFields(data);
        setOriginalFields(data);
        setUserId(user.id || user._id || null);
        setUserMeta({
          role: user.role,
          status: user.status,
          preferences: user.preferences,
        });
        setError(null);
      } else {
        setError(t('notAuthorizedOrFailedToLoadUserInfo'));
      }
    }).catch(() => setError(t('failedToLoadUserInfo')));
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFields({ ...fields, [e.target.name]: e.target.value });
  };

  const handleEdit = () => {
    setOriginalFields(fields);
    setEdit(true);
  };

  const handleCancel = () => {
    setFields(originalFields);
    setEdit(false);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;
    setSaving(true);
    try {
      await http.put(`/api/users/${userId}`, {
        firstName: fields.firstName,
        lastName: fields.lastName,
        phoneNumber: fields.phone,
        role: userMeta.role,
        status: userMeta.status,
        preferences: userMeta.preferences,
      });
      setOriginalFields(fields);
      setEdit(false);
    } catch (err) {
      console.error('Save profile error:', err);
      setError(t('failedToSaveProfile'));
    } finally {
      setSaving(false);
    }
  };

  const firstLetter = fields.email ? fields.email[0].toUpperCase() : undefined;
  return (
    <ProfileLayout activeTab="profile" firstLetter={firstLetter}>
      <Title>{t('profile')}</Title>
      {error ? (
        <div style={{ color: 'red', marginBottom: 24 }}>{error}</div>
      ) : (
        <form onSubmit={handleSave}>
          <Field>
            <Label>{t('firstName')}</Label>
            <Input name="firstName" value={fields.firstName} onChange={handleChange} disabled={!edit} />
          </Field>
          <Field>
            <Label>{t('lastName')}</Label>
            <Input name="lastName" value={fields.lastName} onChange={handleChange} disabled={!edit} />
          </Field>
          <Field>
            <Label>{t('email')}</Label>
            <Input name="email" value={fields.email} onChange={handleChange} disabled={!edit} />
          </Field>
          <Field>
            <Label>{t('password')}</Label>
            <Input name="password" value={fields.password} onChange={handleChange} disabled={!edit} type="password" />
          </Field>
          <Field>
            <Label>{t('mobilePhone')}</Label>
            <Input name="phone" value={fields.phone} onChange={handleChange} disabled={!edit} />
          </Field>
          {!edit ? (
            <Button type="button" onClick={handleEdit}>{t('edit')}</Button>
          ) : (
            <>
              <Button type="submit" disabled={saving}>{saving ? '...' : t('save')}</Button>
              <Button type="button" onClick={handleCancel} style={{background: '#aaa'}}>{t('cancel')}</Button>
            </>
          )}
        </form>
      )}
    </ProfileLayout>
  );
} 